'use client';

import Image from 'next/image';
import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useBottomFixedOffset } from './useBottomFixedOffset';
import styles from './StickyPurchaseBar.module.scss';

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export interface StickyPurchaseBarProps {
  visible: boolean;
  name: string;
  imageUrl?: string;
  price?: string;
  /** Receives focus when the bar hides while focus is inside it. */
  returnFocusTo?: HTMLElement | null;
  /** The on-page primary CTA, rendered with the same label, state and handler. */
  children: ReactNode;
}

export function StickyPurchaseBar({
  visible,
  name,
  imageUrl,
  price,
  returnFocusTo,
  children,
}: Readonly<StickyPurchaseBarProps>) {
  const barRef = useRef<HTMLDivElement>(null);
  const [isRendered, setIsRendered] = useState(visible);

  if (visible && !isRendered) {
    setIsRendered(true);
  }

  // Unmount one commit after `visible` flips so focus can be handed back first;
  // removing a focused node would otherwise drop focus to <body>.
  useIsomorphicLayoutEffect(() => {
    if (visible || !isRendered) return;
    if (barRef.current?.contains(document.activeElement)) {
      returnFocusTo?.focus({ preventScroll: true });
    }
    setIsRendered(false);
  }, [visible, isRendered, returnFocusTo]);

  useBottomFixedOffset(isRendered);

  if (!isRendered || typeof document === 'undefined') return null;

  // Portalled to <body> so no transformed PDP ancestor can re-anchor `position: fixed`,
  // and so the spacer lands after the footer instead of mid-page.
  return createPortal(
    <>
      <div className={styles.spacer} aria-hidden="true" />
      <div ref={barRef} className={styles.bar} data-testid="sticky-purchase-bar">
        <div className={styles.content}>
          <div className={styles.recap}>
            {imageUrl && (
              <span className={styles.thumbnail} aria-hidden="true">
                <Image src={imageUrl} alt="" width={90} height={90} sizes="90px" />
              </span>
            )}
            <p className={styles.name}>{name}</p>
          </div>
          <div className={styles.purchase}>
            {price && <span className={styles.price}>{price}</span>}
            <div className={styles.cta}>{children}</div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
